import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { UserAvatar } from '@/components/UserAvatar';
import { StudioName } from '@/components/StudioName';
import { SEO } from '@/components/SEO';
import {
    Trophy,
    Clock,
    Gamepad2,
    Award,
    Heart,
    User,
    Calendar,
    Sparkles
} from 'lucide-react';
import { getPublicPlayerProfile, getPublicPlayerFavorites, type PublicPlayerProfile } from '@/services/publicPlayerService';
import { PLAYER_LEVELS, BADGE_TIERS } from '@/data/missions';
import { Gems } from '@/components/Gems';
import { getCountryFlag } from '@/utils/countryMapping';
import { ProgressBar, CircularProgress } from '@/components/ProgressBar';
import { Button } from '@/components/ui/button';

export default function PublicPlayerProfile() {
    const { playerName } = useParams<{ playerName: string }>();
    const navigate = useNavigate();
    const [profile, setProfile] = useState<PublicPlayerProfile | null>(null);
    const [favorites, setFavorites] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [notFound, setNotFound] = useState(false);

    // Calculate total available badges dynamically
    const totalAvailableBadges = Object.values(BADGE_TIERS).reduce((acc, category) => acc + category.length, 0);

    useEffect(() => {
        if (playerName) {
            loadPlayerProfile();
        }
    }, [playerName]);

    const loadPlayerProfile = async () => {
        if (!playerName) return;

        setLoading(true);
        setNotFound(false);

        try {
            const playerProfile = await getPublicPlayerProfile(playerName);

            if (!playerProfile) {
                setNotFound(true);
                setLoading(false);
                return;
            }

            setProfile(playerProfile);

            // Load favorites
            const playerFavorites = await getPublicPlayerFavorites(playerProfile.id);
            setFavorites(playerFavorites);
        } catch (error) {
            console.error('Error loading player profile:', error);
            setNotFound(true);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="container mx-auto px-4 py-8">
                    <div className="animate-pulse space-y-4">
                        <div className="h-8 bg-muted rounded w-1/3"></div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {[1, 2, 3].map((i) => (
                                <div key={i} className="h-32 bg-muted rounded"></div>
                            ))}
                        </div>
                    </div>
                </div>
            </Layout>
        );
    }

    if (notFound || !profile) {
        return (
            <Layout>
                <SEO
                    title="Player Not Found"
                    description="The requested player profile could not be found."
                />
                <div className="container mx-auto px-4 py-8">
                    <Card className="p-8 text-center">
                        <User className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
                        <h1 className="text-2xl font-bold mb-2">Player Not Found</h1>
                        <p className="text-muted-foreground mb-4">
                            The player "{playerName}" could not be found.
                        </p>
                    </Card>
                </div>
            </Layout>
        );
    }

    const levelInfo = PLAYER_LEVELS[profile.level as keyof typeof PLAYER_LEVELS];
    const memberSince = new Date(profile.created_at).toLocaleDateString('en-US', {
        month: 'long',
        year: 'numeric'
    });

    const metaDescription = profile.bio
        ? `${profile.bio.substring(0, 150)}${profile.bio.length > 150 ? '...' : ''}`
        : `${profile.display_name} is a player on IndieKeys with ${profile.badges.length} badges earned, ${profile.games_tested} games tested, and ${Math.round(profile.total_playtime / 60)} hours of playtime.`;

    return (
        <Layout>
            <SEO
                title={`${profile.display_name} - IndieKeys Player Profile`}
                description={metaDescription}
            />
            {/* Structured Data for SEO */}
            <script type="application/ld+json">
                {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "ProfilePage",
                    "mainEntity": {
                        "@type": "Person",
                        "name": profile.display_name,
                        "description": profile.bio || `IndieKeys player with ${profile.badges.length} badges`,
                        "image": profile.avatar_url,
                        "url": window.location.href,
                        "sameAs": [
                            profile.twitch_url,
                            profile.youtube_url,
                            profile.tiktok_url,
                            profile.instagram_url,
                            profile.twitter_url,
                            profile.bluesky_url,
                            profile.steam_url
                        ].filter(Boolean)
                    }
                })}
            </script>
            <div className="container mx-auto px-4 py-8">
                <div>
                    <Card className="p-6 h-full rounded-custom-paddings bg-transparent">
                        <div className="flex flex-col items-center text-center">
                            {/* Avatar with UserAvatar component */}
                            <div className='flex flex-col md:flex-row items-center justify-between w-full p-2'>
                                <div className='flex items-center gap-3'>
                                    <UserAvatar
                                        src={profile.avatar_url || undefined}
                                        fallback={profile.display_name.charAt(0).toUpperCase()}
                                        size="lg"
                                        borderStyle={profile.player_border || undefined}
                                    />

                                    <div className="text-left">
                                        {/* Display Name - H1 for SEO */}
                                        <div className="flex items-end gap-2">
                                            <h1 className="text-3xl font-bold">{profile.display_name}</h1>
                                            <div className="flex items-center justify-center text-primary font-bold text-sm">
                                                Lv. {profile.player_level}
                                            </div>
                                        </div>

                                        <div className='flex gap-3 items-center'>
                                            {/* Country */}
                                            {profile.country && getCountryFlag(profile.country) && (
                                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                                    <img
                                                        src={getCountryFlag(profile.country) || ''}
                                                        alt={profile.country}
                                                        className="w-5 h-4 object-cover rounded"
                                                        title={profile.country}
                                                    />
                                                    <span>{profile.country}</span>
                                                </div>
                                            )}
                                            {/* Social Media Links */}
                                            {(profile.twitch_url || profile.youtube_url || profile.tiktok_url || profile.instagram_url || profile.twitter_url || profile.bluesky_url || profile.steam_url) && (
                                                <div className="flex items-center gap-2 justify-center">
                                                    {profile.steam_url && (
                                                        <a href={profile.steam_url} target="_blank" rel="noopener noreferrer" className="text-foreground hover:text-primary transition-colors">
                                                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M11.979 0C5.678 0 .511 4.86.022 11.037l6.432 2.658c.545-.371 1.203-.59 1.912-.59.063 0 .125.004.188.006l2.861-4.142V8.91c0-2.495 2.028-4.524 4.524-4.524 2.494 0 4.524 2.031 4.524 4.527s-2.03 4.525-4.524 4.525h-.105l-4.076 2.911c0 .052.004.105.004.159 0 1.875-1.515 3.396-3.39 3.396-1.635 0-3.016-1.173-3.331-2.727L.436 15.27C1.862 20.307 6.486 24 11.979 24c6.627 0 11.999-5.373 11.999-12S18.605 0 11.979 0zM7.54 18.21l-1.473-.61c.262.543.714.999 1.314 1.25 1.297.539 2.793-.076 3.332-1.375.263-.63.264-1.319.005-1.949s-.75-1.121-1.377-1.383c-.624-.26-1.29-.249-1.878-.03l1.523.63c.956.4 1.409 1.5 1.009 2.455-.397.957-1.497 1.41-2.454 1.012H7.54zm11.415-9.303c0-1.662-1.353-3.015-3.015-3.015-1.665 0-3.015 1.353-3.015 3.015 0 1.665 1.35 3.015 3.015 3.015 1.663 0 3.015-1.35 3.015-3.015zm-5.273-.005c0-1.252 1.013-2.266 2.265-2.266 1.249 0 2.266 1.014 2.266 2.266 0 1.251-1.017 2.265-2.266 2.265-1.253 0-2.265-1.014-2.265-2.265z" /></svg>
                                                        </a>
                                                    )}
                                                    {profile.twitch_url && (
                                                        <a href={profile.twitch_url} target="_blank" rel="noopener noreferrer" className="text-purple-500 hover:text-purple-400 transition-colors">
                                                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z" /></svg>
                                                        </a>
                                                    )}
                                                    {profile.youtube_url && (
                                                        <a href={profile.youtube_url} target="_blank" rel="noopener noreferrer" className="text-red-500 hover:text-red-400 transition-colors">
                                                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" /></svg>
                                                        </a>
                                                    )}
                                                    {profile.tiktok_url && (
                                                        <a href={profile.tiktok_url} target="_blank" rel="noopener noreferrer" className="text-foreground hover:text-primary transition-colors">
                                                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" /></svg>
                                                        </a>
                                                    )}
                                                    {profile.instagram_url && (
                                                        <a href={profile.instagram_url} target="_blank" rel="noopener noreferrer" className="text-pink-500 hover:text-pink-400 transition-colors">
                                                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678c-3.405 0-6.162 2.76-6.162 6.162 0 3.405 2.76 6.162 6.162 6.162 3.405 0 6.162-2.76 6.162-6.162 0-3.405-2.76-6.162-6.162-6.162zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405c0 .795-.646 1.44-1.44 1.44-.795 0-1.44-.646-1.44-1.44 0-.794.646-1.439 1.44-1.439.793-.001 1.44.645 1.44 1.439z" /></svg>
                                                        </a>
                                                    )}
                                                    {profile.twitter_url && (
                                                        <a href={profile.twitter_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 transition-colors">
                                                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
                                                        </a>
                                                    )}
                                                    {profile.bluesky_url && (
                                                        <a href={profile.bluesky_url} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-400 transition-colors">
                                                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.815 2.736 3.713 3.66 6.383 3.364.136-.02.275-.039.415-.056-.138.022-.276.04-.415.056-3.912.58-7.387 2.005-2.83 7.078 5.013 5.19 6.87-1.113 7.823-4.308.953 3.195 2.05 9.271 7.733 4.308 4.267-4.308 1.172-6.498-2.74-7.078a8.741 8.741 0 0 1-.415-.056c.14.017.279.036.415.056 2.67.297 5.568-.628 6.383-3.364.246-.828.624-5.79.624-6.478 0-.69-.139-1.861-.902-2.206-.659-.298-1.664-.62-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8Z" /></svg>
                                                        </a>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => navigate('/ranking')}
                                    className="bg-muted/30 hover:bg-card border border-border/50 gap-2 transition-all duration-300 hover:text-primary mt-4 md:mt-0"
                                >
                                    <Trophy className="w-4 h-4 text-yellow-500" />
                                    <span className="font-semibold">Rankings</span>
                                </Button>
                            </div>

                            {/* Bio */}
                            {/* {profile.bio && (
                                <p className="text-xs text-muted-foreground mb-4 line-clamp-3">
                                    {profile.bio}
                                </p>
                            )} */}
                        </div>
                    </Card>
                    {/* Main Content */}
                    <div className="lg:col-span-8 xl:col-span-9 space-y-4">
                        {/* Stats Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* Gaming Progress Card */}
                            <Card className="group relative overflow-hidden p-4 bg-gradient-to-br from-muted/30 to-muted/10 border-border/50 rounded-custom-paddings !px-6 !py-5">
                                <div className="absolute inset-0 bg-gradient-to-br from-primary/15 via-transparent to-transparent opacity-100" />
                                <div className="relative z-10">
                                    <h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
                                        <Gamepad2 className="w-5 h-5 text-primary" />
                                        Gaming Progress
                                    </h2>
                                    <div className="space-y-3">
                                        <div className="flex justify-between items-center p-2 rounded-lg bg-muted/20 border border-border/30">
                                            <span className="text-sm text-muted-foreground">Games Purchased</span>
                                            <span className="font-bold text-lg text-primary">{profile.purchased_games_count}</span>
                                        </div>
                                        <div className="flex justify-between items-center p-2 rounded-lg bg-muted/20 border border-border/30">
                                            <span className="text-sm text-muted-foreground">Games Tested</span>
                                            <span className="font-bold text-lg text-primary">{profile.games_tested}</span>
                                        </div>
                                        <div className="flex justify-between items-center p-2 rounded-lg bg-muted/40">
                                            <div className="flex-1">
                                                <p className="text-sm text-muted-foreground leading-tight">Completion Rate</p>
                                                <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider font-semibold">Games played ratio</p>
                                            </div>
                                            <CircularProgress
                                                value={profile.purchased_games_count > 0 ? Math.round((profile.games_tested / profile.purchased_games_count) * 100) : 0}
                                                max={100}
                                                size={42}
                                                strokeWidth={4}
                                            />
                                        </div>
                                        <div className="flex justify-between items-center p-2 rounded-lg bg-muted/40">
                                            <div className="flex-1">
                                                <p className="text-sm text-muted-foreground leading-tight">Achievements Progress</p>
                                                <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider font-semibold">Total trophies earned</p>
                                            </div>
                                            <CircularProgress
                                                value={profile.achievement_progress}
                                                max={100}
                                                size={42}
                                                strokeWidth={4}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </Card>

                            {/* Community & Activity Card */}
                            <Card className="group relative overflow-hidden p-4 bg-gradient-to-br from-muted/30 to-muted/10 border-border/50 rounded-custom-paddings !px-6 !py-5">
                                <div className="absolute inset-0 bg-gradient-to-br from-primary/15 via-transparent to-transparent opacity-100" />
                                <div className="relative z-10">
                                    <h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
                                        <Heart className="w-5 h-5 text-primary" />
                                        Community & Activity
                                    </h2>
                                    <div className="space-y-3">
                                        {/* <div className="flex justify-between items-center p-2 rounded-lg bg-muted/20 border border-border/30">
                                            <div className="flex-1">
                                                <p className="text-sm text-muted-foreground">Member Since</p>
                                            </div>
                                            <div className="font-bold text-md text-primary">
                                                {memberSince}
                                            </div>
                                        </div> */}
                                        <div className="flex justify-between items-center p-2 rounded-lg bg-muted/20 border border-border/30">
                                            <span className="text-sm text-muted-foreground">Favorite Games</span>
                                            <span className="font-bold text-lg text-primary">{profile.favorites_count}</span>
                                        </div>
                                        <div className="flex justify-between items-center p-2 rounded-lg bg-muted/20 border border-border/30">
                                            <span className="text-sm text-muted-foreground">Total Playtime</span>
                                            <span className="font-bold text-lg text-primary">{Math.round(profile.total_playtime / 60)}h</span>
                                        </div>
                                        <div className="flex justify-between items-center p-2 rounded-lg bg-muted/20 border border-border/30">
                                            <span className="text-sm text-muted-foreground">Missions Done</span>
                                            <span className="font-bold text-lg text-primary">{profile.missions_count}</span>
                                        </div>
                                        <div className="flex justify-between items-center p-2 rounded-lg bg-muted/20 border border-border/30">
                                            <div className="flex-1">
                                                <p className="text-sm text-muted-foreground leading-tight">Badges Earned</p>
                                                <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider font-semibold mb-1">Collection progress</p>
                                                <ProgressBar value={profile.badges.length} max={totalAvailableBadges} color="primary" className="h-1.5" />
                                            </div>
                                            <div className="flex items-center gap-2 text-primary ml-4">
                                                <span className="font-bold text-lg">{profile.badges.length}/{totalAvailableBadges}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        </div>

                        {/* Badges Section */}
                        <Card className="rounded-custom-paddings !px-6 !py-5 transition-all duration-300 hover:shadow-lg">
                            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                                <Award className="w-5 h-5 text-primary" />
                                Earned Badges
                            </h2>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                                {profile.badges.map((badgeName, index) => {
                                    // Find the badge in BADGE_TIERS
                                    let badgeInfo = null;
                                    for (const category of Object.values(BADGE_TIERS)) {
                                        const found = category.find((tier: any) => tier.name === badgeName);
                                        if (found) {
                                            badgeInfo = found;
                                            break;
                                        }
                                    }

                                    return (
                                        <div
                                            key={index}
                                            className="group relative p-3 border rounded-custom-paddings bg-muted/30 border-border/30 transition-all duration-300 hover:bg-muted/40 hover:border-primary/20"
                                            style={{
                                                animationDelay: `${index * 50}ms`
                                            }}
                                        >
                                            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-custom-paddings" />
                                            <div className="relative flex items-center gap-3">
                                                <div className="text-2xl flex-shrink-0 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6">
                                                    {badgeInfo?.icon}
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-medium text-sm text-foreground/80 break-words group-hover:text-foreground transition-colors">
                                                        {badgeInfo?.name}
                                                    </p>
                                                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
                                                        {badgeInfo?.description}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                                {profile.badges.length === 0 && (
                                    <p className="col-span-full text-center text-muted-foreground py-8">
                                        No badges earned yet
                                    </p>
                                )}
                            </div>
                        </Card>

                        {/* Favorite Games Section */}
                        {favorites.length > 0 && (
                            <Card className="rounded-custom-paddings !px-6 !py-5 transition-all duration-300 hover:shadow-lg">
                                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                                    <Heart className="w-5 h-5 text-primary" />
                                    Favorite Games
                                </h2>
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {favorites.map((favorite) => (
                                        <div
                                            key={favorite.id}
                                            onClick={() => navigate(`/game/${favorite.game?.id}`)}
                                            className="group relative flex items-center gap-3 p-2 border rounded-custom-paddings bg-muted/30 border-border/30 hover:border-primary/50 hover:bg-muted/50 transition-all duration-300 cursor-pointer overflow-hidden"
                                        >
                                            <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                            {favorite.game?.cover_image_url && (
                                                <img
                                                    src={favorite.game.cover_image_url}
                                                    alt={favorite.game.title}
                                                    className="w-16 h-10 object-cover rounded-md shadow-sm transition-transform duration-500 group-hover:scale-110"
                                                />
                                            )}
                                            <div className="flex-1 min-w-0 relative z-10">
                                                <p className="font-semibold text-sm truncate group-hover:text-primary transition-colors">
                                                    {favorite.game?.title}
                                                </p>
                                                <p className="text-xs text-muted-foreground">
                                                    <StudioName
                                                        studioName={favorite.game?.developer?.studio_name}
                                                        displayName={favorite.game?.developer?.display_name || 'Unknown'}
                                                        isPremium={favorite.game?.developer?.is_premium}
                                                        premiumEndDate={favorite.game?.developer?.premium_end_date}
                                                        size="sm"
                                                    />
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Card>
                        )}

                        {/* Recent Activity Section */}
                        <Card className="rounded-custom-paddings !px-6 !py-5 transition-all duration-300 hover:shadow-lg">
                            <h2 className="text-lg font-semibold mb-6 flex items-center gap-2">
                                <Clock className="w-5 h-5 text-primary" />
                                Recent Activity
                            </h2>
                            <div className="relative space-y-6 before:absolute before:inset-y-0 before:left-[11px] before:w-[2px] before:bg-muted/50">
                                {/* Activity 1: Joined */}
                                <div className="relative pl-8">
                                    <div className="absolute left-0 top-1 w-6 h-6 rounded-full bg-background border-2 border-primary flex items-center justify-center z-10">
                                        <User className="w-3 h-3 text-primary" />
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium">Joined IndieKeys</p>
                                        <p className="text-xs text-muted-foreground">{memberSince}</p>
                                    </div>
                                </div>

                                {/* Activity 2: Badges (grouped) */}
                                {profile.badges.length > 0 && (
                                    <div className="relative pl-8">
                                        <div className="absolute left-0 top-1 w-6 h-6 rounded-full bg-background border-2 border-green-500 flex items-center justify-center z-10">
                                            <Award className="w-3 h-3 text-green-500" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium">Earned {profile.badges.length} badges</p>
                                            <p className="text-xs text-muted-foreground">Throughout the journey</p>
                                        </div>
                                    </div>
                                )}

                                {/* Activity 3: Testing */}
                                {profile.games_tested > 0 && (
                                    <div className="relative pl-8">
                                        <div className="absolute left-0 top-1 w-6 h-6 rounded-full bg-background border-2 border-purple-500 flex items-center justify-center z-10">
                                            <Gamepad2 className="w-3 h-3 text-purple-500" />
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium">Tested {profile.games_tested} games</p>
                                            <p className="text-xs text-muted-foreground">Helping developers grow</p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </Card>
                    </div>
                </div>
            </div>
        </Layout>
    );
}
